package com.neurobs.ccab

import com.neurobs.util.{Guid, PlainDate, SecondsTimestamp}
import io.circe.generic.auto._
import com.neurobs.ccab.db._
import com.neurobs.ccab.scoring.{ConfigData, SubjectScoreData}

case class FindTestRunResult
(
	session: Option[db.SessionData],
	testRun: Option[TestRunLogfileView]
)

case class LogbackEventData
(
	log: String,
	msg: String,
	level: String,
	thread: Option[String],
	arg0: Option[String],
	arg1: Option[String],
	arg2: Option[String],
	arg3: Option[String],
	exc: Option[String]
)

case class CreateAccountData (
	email: String,
	firstName: String,
	lastName: String,
	organization: String,
	description: String
)

case class CreateUserData (
	email: String,
	firstName: String,
	lastName: String
)

case class ResetUserPasswordData
(
	password: String
)

case class UpdateUserData
(
	firstName: String,
	lastName: String,
	accountAdmin: Boolean,
	disabled: Boolean
)

case class LoginData (
	primaryEmail: String,
	username: Option[String],
	password: String
)

case class ValidUserData
(
	userId: Long,
	username: String,
	accountId: Long,
	isSiteAdmin: Boolean,
	isRootUser: Boolean
) {
	def isAccountAdmin(accountId: Long): Boolean = isSiteAdmin || (isRootUser && (accountId == this.accountId))
	def userDisplay: String = if (isRootUser) "Primary" else username
}

object ValidUserData {
	def fromIdentity(data: IdentityData): ValidUserData = ValidUserData(
		userId = data.legacyUserId,
		username = data.email,
		accountId = data.legacyAccountId,
		isSiteAdmin = data.isSiteAdmin,
		isRootUser = data.isRootUser
	)
}

case class ControllerAppData (
	controllerName: String,
	computerName: String,
	requestIp: String,
	lanIp: String
)

/** This was previously sent to TestStation on its web socket after connection
 */
case class ControllerSessionStart (
	sessionData: Option[ControllerSessionData],
	controllerToken: Option[String],
	error: Option[String]
)

object ControllerSessionStart {
	def error(msg: String) = ControllerSessionStart(None, None, Some(msg))
	def valid(session: ControllerSessionData, token: Option[String]) = ControllerSessionStart(Some(session), token, None)
}

/** this is provided to Examiner when connecting to a test station */
case class ControllerSessionTokenData
(
	opentokToken: Option[OpentokTokenData] = None,
	agoraToken: Option[AgoraTokenData] = None,
	clientId: Option[ChatClientId] = None,
	error: Option[String] = None
)

case class CaasTokenData
(
	opentokToken: Option[OpentokTokenData] = None,
	agoraToken: Option[AgoraTokenData] = None
)

/** This is sent to Test Station on its web socket after it connects */
case class ControllerSessionStart2
(
	sessionData: Option[ControllerSessionData],
	token: Option[CaasTokenData],
	syncData: Option[TestStationSyncData],
	error: Option[String]
)

object ControllerSessionStart2 {
	def error(msg: String) = ControllerSessionStart2(sessionData = None, token = None, syncData = None, error = Some(msg))
	def valid(session: ControllerSessionData, token: Option[CaasTokenData], syncData: Option[TestStationSyncData]) = ControllerSessionStart2(sessionData = Some(session), token = token, syncData = syncData, error = None)
}

/** This is sent to Test Station on its web socket after it connects */
case class ControllerSessionStart3
(
	sessionData: Option[ControllerSessionData],
	token: Option[CaasTokenData],
	syncData: Option[TestStationSyncData2],
	error: Option[String]
)

object ControllerSessionStart3 {
	def error(msg: String) = ControllerSessionStart3(sessionData = None, token = None, syncData = None, error = Some(msg))
	def valid(session: ControllerSessionData, token: Option[CaasTokenData], syncData: Option[TestStationSyncData2]) = ControllerSessionStart3(sessionData = Some(session), token = token, syncData = syncData, error = None)
}

case class ReleaseDataRest
(
	id: Long,
	createdAt: SecondsTimestamp = SecondsTimestamp.now(),
	releaseTypeId: Long,
	active: Boolean,
	version: String,
	storageKey: Option[String],
	url: Option[String],
	dependency1MinVersion: String,
	releaseNotes: String,
	buildId: Option[Guid],
	releaseDate: Option[PlainDate]
)

object ReleaseDataRest {
	def apply(data: ReleaseData, urlPrefix: String): ReleaseDataRest = ReleaseDataRest(
		id = data.id,
		createdAt = data.createdAt,
		releaseTypeId = data.releaseTypeId,
		active = data.active,
		version = data.version,
		storageKey = data.storageKey,
		url = data.storageKey.map(u => s"$urlPrefix/$u"),
		dependency1MinVersion = data.dependency1MinVersion,
		releaseNotes = data.releaseNotes,
		buildId = data.buildId,
		releaseDate = data.releaseDate
	)
}

case class ReleaseUpdateData (
	active: Boolean,
	releaseDate: Option[PlainDate],
	dependency1MinVersion: String,
	releaseNotes: String
)

case class CreateReleaseResult (
	error: Option[String] = None,
	release: Option[ReleaseDataRest] = None
)



case class TestUpdateData
(
	identifier: String,
	displayName: String,
	description: String
)

case class ExaminerTagCreateData
(
	identifier: String,
	groupName: String,
	displayNameTestRun: Option[String],
	displayNameSession: Option[String],
	description: String
)

case class ExaminerTagUpdateData
(
	groupName: String,
	displayNameTestRun: Option[String],
	displayNameSession: Option[String],
	description: String
)

case class StudyView
(
	id: Long,
	displayName: String,
	description: String,
	studyTypeId: Long,
	studyTypeDisplayName: String,
	studyTypeDescription: String,
)

case class StudyTypeUpdateData
(
	displayName: String,
	description: String
)

case class StudyUpdateData
(
	displayName: String,
	description: String,
	studyTypeId: Long,
	active: Boolean,
	config: Option[String]
)

case class GeneralResult
(
	error: Option[String] = None
)

case class ObjectResult[DataType]
(
	error: Option[String] = None,
	data: Option[DataType] = None
)

case class SubjectCreateData
(
	data: SubjectIdentityData,
	dataPassword: String,
	reportingId: Option[Guid]
)

case class SubjectUpdateData
(
	data: SubjectIdentityData,
	dataPassword: String
)

case class SubjectFindData
(
	data: SubjectIdentityData,
	dataPassword: String
)

case class AccountSubjectResponse
(
	id: Long,
	accountId: Long,
	subjectId: Long,
	createdAt: SecondsTimestamp,
	reportingId: Guid,
	identity: Option[SubjectIdentityData]
) {
	def displayFullName: String = {
		identity match {
			case None => ""
			case Some(i) =>
				if (i.lastName.isDefined)
					s"${i.firstName.getOrElse("")} ${i.lastName.getOrElse("")}"
				else
					i.localID.getOrElse("")
		}
	}
}

case class TestSessionParameter
(
	// This is the display name, not the identifier
	name: String,
	value: String
)

case class TestSessionProperties
(
	group: String,
	sequence: String,
	label: Option[String],
	config: Option[String],
	params: List[TestSessionParameter]
) {
	def terseSummary: String = {
		val c = config.map(s => s" ($s)").getOrElse("")
		val front = s"$group, $sequence$c"
		val back = params.map(p => s"${p.name}: ${p.value}").mkString(", ")
		List(front, back).mkString(", ")
	}
}

object TestSessionProperties {
	def fromJson(json: String): Option[TestSessionProperties] = {
		io.circe.parser.decode[TestSessionProperties](json) match {
			case Left(_) => None
			case Right(data) => Some(data)
		}
	}
}

case class TestSessionResponse
(
	session: db.SessionData,
	accountName: String,
	studyName: String,
	adminUserName: Option[String],
	reportingId: Guid
)

object LocationType extends Enumeration {
	val Home = Value("home")
	val Lab = Value("lab")
	val Office = Value("office")
}

case class TestSessionCreateData
(
	accountId: Long,
	studyId: Long,
	accountSubjectId: Long,
	adminUserId: Option[Long],
	stationName: String,
	properties: Option[String],
	startTime: SecondsTimestamp,
	locationType: Option[LocationType.Value]
)

case class TestRunDataCreateData
(
	accessGuid: Guid,
	runError: Option[String],
	localLogfileFilename: String,
	testIdentifier: Option[String]
)

case class TestRunDataCreateFromMetadataData
(
	accessGuid: Guid,
	runError: Option[String],
	localLogfileFilename: String,
	logfileMetadataJson: String
)

case class SessionLogCreateData
(
	accessGuid: Guid,
	keyIdentifier: Option[String]
)


case class SessionLogCreateData2
(
	subjectGuid: Guid,
	keyIdentifier: Option[String],
	login: LoginData
)

case class SessionLogCreateData3
(
	subjectGuid: Guid,
	keyIdentifier: Option[String]
)

case class TestStationSyncData2
(
	bucket: String,
	region: String,
	systemSyncFolder: String,
	sessionsFolder: String,
	corruptedFilesFolder: String,
	unencryptedSessionsFolder: String,
	dataEncryptionKey: String,
	dataArchiveLimitDays: Double
)

case class TestStationSyncData
(
	accessKey: String,
	secretKey: String,
	bucket: String,
	region: String,
	systemSyncFolder: String,
	sessionsFolder: String,
	corruptedFilesFolder: String,
	unencryptedSessionsFolder: String,
	dataEncryptionKey: String,
	dataArchiveLimitDays: Double
) {
	/** Appends system-specific folders to the system sync folder
	 *
	 * @param computerId String identifying the test system
	 * @param accountId Account ID used for syncing
	 */
	def finalize(computerId: String, accountId: Long): TestStationSyncData = {
		val allowedPunct = "!-_.*'()"
		val safeId = computerId.trim.map { c =>
			if (c.isLetterOrDigit || allowedPunct.contains(c)) c else '.'
		}
		copy(
			systemSyncFolder = s"$systemSyncFolder/$accountId/$safeId",
			corruptedFilesFolder = s"$corruptedFilesFolder/$accountId/$safeId"
		)
	}

	def finalize2(computerId: String, accountId: Long): TestStationSyncData2 = {
		val temp = finalize(computerId = computerId, accountId = accountId)
		TestStationSyncData2(
			bucket = temp.bucket,
			region = temp.region,
			systemSyncFolder = temp.systemSyncFolder,
			sessionsFolder = temp.sessionsFolder,
			corruptedFilesFolder = temp.corruptedFilesFolder,
			unencryptedSessionsFolder = temp.unencryptedSessionsFolder,
			dataEncryptionKey = temp.dataEncryptionKey,
			dataArchiveLimitDays = temp.dataArchiveLimitDays
		)
	}
}

case class SessionViewResult
(
	session: SessionView,
	synced: Option[Boolean],
	identity: Option[SubjectIdentityData]
)

case class SessionSearchData
(
	studyTypeId: Option[Long] = None,
	accountId: Option[Long] = None,
	studyId: Option[Long] = None,
	accountSubjectId: Option[Long] = None,
	dataPassword: Option[String] = None,
	getSyncedStatus: Boolean = false,
	getIdentityData: Boolean = false
)

case class UpdateSessionsData
(
	studyId: Long,
	accountSubjectId: Long,
	sessionIds: Seq[Long]
)

case class SessionSearchResultData
(
	search: SessionSearchData,
	sessions: Seq[SessionViewResult] = Vector()
)

case class TestRunDisplayData
(
	testRun: TestRunData,
	testPackageVersion: Option[String],
	testName: Option[String]
)

case class SessionDisplayData
(
	session: SessionViewResult,
	testRuns: Seq[TestRunDisplayData],
	adminUsername: Option[String]
)

case class SubjectSessionsData
(
	sessions: Seq[SessionDisplayData]
)

case class SubjectSessionsRequestData
(
	accountId: Long,
	accountSubjectId: Long,
	dataPassword: Option[String]
)

case class TestRunSearchData
(
	runError: String,
	studyTypeId: Long
)

case class ErrorCreateData
(
	eventTime: SecondsTimestamp,
	`class`: String,
	`type`: String,
	content: String,
	contentType: String
)

case class ErrorSearchData
(
	minTime: Option[SecondsTimestamp] = None,
	maxTime: Option[SecondsTimestamp] = None,
	errorClassId: Option[Long] = None,
	errorTypeId: Option[Long] = None,
	contentContains: Option[String] = None,
	maxId: Option[Long] = None,
	limit: Int = 20
)

case class FileTranscriptRequestSearchData
(
	requestCompleted: Option[Boolean] = None,
	isRetry: Option[Boolean] = None,
	hasError: Option[Boolean] = None,
	responseTypeId: Option[Long] = None,
	responseProcessed: Option[Boolean] = None,
	asrEngineId: Option[Long] = None,
	minTime: Option[SecondsTimestamp] = None,
	maxTime: Option[SecondsTimestamp] = None,
	maxId: Option[Long] = None,
	limit: Int = 20
)

case class EventServerEventSearchData
(
	eventType: Option[String] = None,
	source: Option[String] = None,
	processed: Option[Boolean] = None,
	hasError: Option[Boolean] = None,
	isRetry: Option[Boolean] = None,
	minTime: Option[SecondsTimestamp] = None,
	maxTime: Option[SecondsTimestamp] = None,
	maxId: Option[Long] = None,
	limit: Int = 20
)

case class TestTranscriptSearchData
(
	completed: Option[Boolean] = None,
	hasError: Option[Boolean] = None,
	processed: Option[Boolean] = None,
	minTime: Option[SecondsTimestamp] = None,
	maxTime: Option[SecondsTimestamp] = None,
	maxId: Option[Long] = None,
	limit: Int = 20
)

case class TestTranscriptMetaData
(
	id: Long,
	createdAt: SecondsTimestamp = SecondsTimestamp.now(),
	configVersion: Int,
	softwareVersion: Int,
	testRunId: Long,
	completedAt: Option[SecondsTimestamp],
	error: Option[String],
	processed: Option[SecondsTimestamp],
	needsSync: Boolean
)

case class CommentLogComment
(
	reportingId: Guid,
	subjectGuid: Guid,
	utcTime: String,
	testName: Option[String],
	comment: String,
	admin: String,
	logfile: Option[String]
)

case class SettingValueCreateData
(
	settingId: Long,
	userId: Option[Long],
	accountId: Option[Long],
	value: Option[String]
)

case class ServerInfo
(
	server_version: String
)

case class CasrInfo
(
	software_version: Int,
	config_version: Int
)

case class EventServerGeneralEventData
(
	id: Long,
	createdAt: SecondsTimestamp,
	eventTime: Option[SecondsTimestamp],
	receivedTime: SecondsTimestamp,
	eventType: Option[String],
	eventSource: Option[String],
	eventFramework: String,
	processed: Option[SecondsTimestamp],
	errorId: Option[Long],
	retryId: Option[Long]
)

case class TranscriptTotalUnprocessed
(
	fileIncompleteCount: Int,
	fileUnprocessedCount: Int,
	testIncompleteCount: Int,
	testUnprocessedCount: Int
)

case class FileTranscriptEngineTotals
(
	asrEngineId: Long,
	successCount: Int,
	failedCount: Int,
)

case class FileTranscriptIntervalTotals
(
	interval: SecondsTimestamp.Interval,
	incompleteCount: Int,
	unprocessedCount: Int,
	engines: Seq[FileTranscriptEngineTotals],
	testSuccessCount: Int,
	testFailureCount: Int
)

case class FileTranscriptTotalsRequest
(
	intervals: Seq[SecondsTimestamp.Interval],
	asrEngineIds: Seq[Long]
)

case class SubjectScoreReport
(
	config: ConfigData,
	data: SubjectScoreData
)

case class StudySessionReportMetadata
(
	reportingId: Guid,
	studySessionId: Guid,
	generationTime: SecondsTimestamp,
	localFilename: String
)

case class ReportHistoryData
(
	reports: Seq[StudySessionReportMetadata]
) {
	def studySessions: Seq[Guid] = reports.map(_.studySessionId).distinct
	def latestFile(studySessionId: Guid): Option[String] = reports.filter(_.studySessionId == studySessionId).sortBy(_.generationTime).lastOption.map(_.localFilename)
}

case class ErrorInfo
(
	error: String
)

case class AccountDataRest
(
	id: Long,
	createdAt: String,
	founderEmail: String,
	organization: String,
	userDescription: String,
	accountUuid: Guid,
	testStationPassword: Guid,
	keyRegistryCreated: Boolean
)

object AccountDataRest {
	def apply(data: AccountData): AccountDataRest = AccountDataRest(
		id = data.id,
		createdAt = data.createdAt.isoString,
		founderEmail = data.primaryEmail,
		organization = data.organization,
		userDescription = data.userDescription,
		accountUuid = data.accountUuid,
		testStationPassword = data.testStationPassword,
		keyRegistryCreated = data.keyRegistry.isDefined
	)
}

case class UserDataRest
(
	id: Long,
	userUuid: Guid,
	createdAt: String,
	email: String,
	firstName: String,
	lastName: String,
	disabled: Boolean,
	migrated: Boolean,
	groups: Seq[IdentityGroup.Value]
)

object UserDataRest {
	def apply(data: UserData, groups: Seq[IdentityGroup.Value]): UserDataRest = UserDataRest(
		id = data.id,
		userUuid = data.userUuid,
		createdAt = data.createdAt.isoString,
		email = data.username,
		firstName = data.givenName,
		lastName = data.familyName,
		disabled = data.disabled,
		migrated = data.migrated,
		groups = groups
	)
}

case class AccountUserDataRest
(
	account: AccountDataRest,
	user: UserDataRest
)

object AccountUserDataRest {
	def apply(data: UserAccountViewData): AccountUserDataRest = AccountUserDataRest(
		account = AccountDataRest(
			id = data.accountId,
			createdAt = data.accountCreatedAt.isoString,
			founderEmail = data.primaryEmail,
			organization = data.organization,
			userDescription = "",
			accountUuid = data.accountUuid,
			testStationPassword = Guid.nullGuid,
			keyRegistryCreated = data.keyRegistryCreated != 0
		),
		user = UserDataRest(
			id = data.userId,
			userUuid = data.userUuid,
			createdAt = data.userCreatedAt.isoString,
			email = data.username,
			firstName = data.givenName,
			lastName = data.familyName,
			disabled = data.disabled,
			migrated = data.migrated,
			groups = Seq.empty[IdentityGroup.Value]
		)
	)
}
